import React, { useEffect, useMemo, useRef } from 'react';
import type { ImageSize, Price } from 'types/isomorphic';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex';
import useOriginalSize from '../../../shared/hooks/useOriginalSize';
import useImageDownloadSizes from '../../../shared/api/download/useImageDownloadSizesRequest';
import useDownloadModifiedAsset from '../../../hooks/useDownloadModifiedAsset';
import useDownloadSize from '../../../hooks/stateful/useDownloadSize';
import ModifiedImages from './ModifiedImages/ModifiedImages';
import OriginalImage from './OriginalImage/OriginalImage';
import restrictModifiedSizesForPackTier from '../../../shared/restrictModifiedSizesForPackTier';
import isExtendEdit from '../../../shared/isExtendEdit';
import { SIZE_NAMES } from '../../../constants/sizeNames';
import styles from './ModifiedBuyCard.module.scss';

type Size = Price & { license?: string; modifiedSize?: ImageSize | null };
// `tier` ('low' | 'med' | 'hi') is a SKU-derived getter on any Agreement
// (see src/shared/models/agreement.ts) — derived purely from the SKU pattern,
// not gated by agreement type; undefined when the SKU matches no tier pattern.
type Option = { sizes?: Size[]; type?: string; tier?: string };

interface ModifiedBuyCardProps {
  // true => a-la-carte LICENSE flow (priced original selector, gating active);
  // false => entitled/Premium-Access DOWNLOAD flow ("Also included").
  priced: boolean;
}

// The selected original carries the joined extend rendition; the fallback
// matches OriginalImage's extend default (the LAST size, mirroring legacy)
// so the first paint doesn't flash a different rendition. Module scope keeps
// the component's cyclomatic complexity within budget.
const resolveSelectedOriginal = (
  originalSizes: Size[],
  selectedName?: string
): Size | undefined =>
  originalSizes.find((s) => s.name === selectedName) ??
  originalSizes[originalSizes.length - 1];

const resolveExtendRendition = (extendEdit: boolean, selectedOriginal?: Size) =>
  extendEdit ? selectedOriginal?.modifiedSize : undefined;

const ModifiedBuyCard = ({ priced }: ModifiedBuyCardProps) => {
  const {
    response: { config: { options = [] } = {} } = {},
  }: { response?: { config?: { options?: Option[] } } } = useOnLoad();
  const { originalSize } = useOriginalSize();
  const { selectedOptionIndex = 0 } = useSelectedIndex();
  // genId/index are typed nullable on the shared context (a plain, unmodified
  // asset has neither), but ModifiedBuyCard only renders once a modification
  // exists, so both are always resolved here.
  const { genId, index } = useDownloadModifiedAsset();
  const { getDownloadSizes } = useImageDownloadSizes();
  const { data: downloadSizes } = getDownloadSizes(
    genId as string,
    index as number
  );

  // Index by the selected agreement, not options[0]: in the Premium-Access
  // download flow initialSelectedOptionIndex() can resolve to a non-zero
  // agreement (SRP-preferred PA / first valid).
  const option: Option | null =
    options.length > 0 ? options[selectedOptionIndex] : null;
  const originalSizes = useMemo(
    () => (option?.sizes || []).filter((s) => !s.license),
    [option]
  );
  // The /download-sizes query is the authoritative modified-size (1k/2k/4k)
  // list; the server's teeShirtSize join on the load-data options returns
  // modifiedSize === null for pre-shot/fibo edits. `getDownloadSizes` is a
  // plain useQuery (not useSuspenseQuery, per ADR-017), so `downloadSizes` is
  // undefined until the query resolves and the modified-images section is
  // briefly absent on initial load. Aligning this with ADR-017 requires
  // updating the shared `useImageDownloadSizesRequest` hook (also consumed by
  // `useDownloadSize`), which is broader than this change's scope.
  // Drop comp: free preview, unmapped by modifiedSizeCopy, so it would render a
  // raw "comp" row. Only extend /download-sizes carry it.
  const modifiedSizes = useMemo(
    () =>
      (downloadSizes ?? []).filter(
        (size) => size.sizeName !== SIZE_NAMES.extraSmall
      ),
    [downloadSizes]
  );

  // Extend edits don't run through Fibo, so the 1k/2k/4k selector (and its
  // pack-tier/original-size gates) doesn't apply: their rendition is fixed by
  // the selected original size via the loader's teeShirtSize join.
  const extendEdit = isExtendEdit(modifiedSizes);

  // Entitled download/UltraPack flow: a low-resolution UltraPack entitles only
  // the Standard (1k) modified size; medium/high packs entitle all. Keyed off
  // the selected pack's tier. The priced/a-la-carte flow gates by the
  // selected original size instead (below), so it is unaffected.
  const visibleModifiedSizes = useMemo(
    () =>
      priced
        ? modifiedSizes
        : restrictModifiedSizesForPackTier(modifiedSizes, option?.tier),
    [priced, modifiedSizes, option]
  );

  const selectedOriginal = resolveSelectedOriginal(
    originalSizes,
    originalSize?.name
  );

  // Extend edits have no modified-size selector, so seed the joined rendition
  // here. Keyed on the primitive sizeName (mirroring LicensedModifiedSize) so
  // a new-but-equal object doesn't re-fire; the ref carries the latest object.
  // A missing join seeds undefined — "not known yet", never a fibo fallback.
  const { setDownloadSize } = useDownloadSize();
  const extendRendition = resolveExtendRendition(extendEdit, selectedOriginal);
  const extendRenditionRef = useRef(extendRendition);
  extendRenditionRef.current = extendRendition;
  const extendRenditionSizeName = extendRendition?.sizeName;
  useEffect(() => {
    if (!extendEdit) return;
    setDownloadSize(extendRenditionRef.current ?? undefined);
  }, [extendEdit, extendRenditionSizeName, setDownloadSize]);

  if (!option) return null;

  // Extend edits render no modified section at all: the selected original
  // row details its joined rendition in place (legacy GenAssetSizes drawer).
  const modifiedSection = extendEdit ? null : (
    <ModifiedImages
      modifiedSizes={visibleModifiedSizes}
      // availableModifiedSizes is the fibo a-la-carte original-size gate.
      originalSize={priced ? originalSize : undefined}
    />
  );
  const originalSection = (
    <OriginalImage
      sizes={originalSizes}
      priced={priced}
      defaultToLastSize={extendEdit}
      showModifiedJoin={extendEdit}
    />
  );

  return (
    <div
      className={styles.card}
      data-testid="modified-buy-card"
    >
      {/* ALC/priced: choose the licensed original size first, then the modified
          version. Premium-Access/download: modified first, original "also
          included" below. */}
      {priced ? (
        <>
          {originalSection}
          {modifiedSection}
        </>
      ) : (
        <>
          {modifiedSection}
          {originalSection}
        </>
      )}
    </div>
  );
};

export default ModifiedBuyCard;
